import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

function AdminReviews() {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const { token } = useAuth();
    const [actionLoading, setActionLoading] = useState(null);

    const fetchReviews = async () => {
        try {
            const response = await fetch('https://healthcard.searchisend.com/health_api/get_all_reviews.php', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch reviews');
            }
            const data = await response.json();
            setReviews(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReviews();
    }, [token]);

    const handleToggleApproval = async (id, currentStatus) => {
        setActionLoading(id);
        const newStatus = currentStatus ? 0 : 1; // Toggle status

        try {
            const response = await fetch('https://healthcard.searchisend.com/health_api/approve_review.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ id, is_approved: newStatus })
            });

            if (!response.ok) {
                throw new Error('Failed to update review status');
            }

            // Refresh reviews to get latest state from DB
            await fetchReviews();
        } catch (err) {
            alert(err.message);
        } finally {
            setActionLoading(null);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    if (loading) return <div className="p-8 text-center text-slate-500 font-medium">Loading reviews...</div>;
    if (error) return <div className="p-8 text-center text-red-500 font-medium">Error: {error}</div>;

    return (
        <div className="p-8 max-w-6xl mx-auto">
            <h1 className="text-3xl font-extrabold text-brand-secondary mb-2">Manage Reviews</h1>
            <p className="text-slate-500 mb-8">Approve reviews to have them automatically appear on the public Landing Page.</p>

            {reviews.length === 0 ? (
                <div className="bg-white p-12 rounded-3xl shadow-sm border border-slate-200 text-center">
                    <div className="w-16 h-16 bg-slate-100 text-slate-400 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"></path></svg>
                    </div>
                    <h2 className="text-xl font-bold text-slate-700">No Reviews Found</h2>
                    <p className="text-slate-500">When users submit reviews from the homepage, they will appear here.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {reviews.map((review) => (
                        <div key={review.id} className="bg-white rounded-3xl p-6 shadow-md border border-slate-200 flex flex-col">
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h3 className="font-bold text-lg text-brand-secondary line-clamp-1">{review.name}</h3>
                                    <div className="text-xs text-slate-400 font-medium flex items-center gap-1 mt-1">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                                        {formatDate(review.createdAt)}
                                    </div>
                                </div>
                                <span className={`px-2 py-1 rounded-full text-xs font-bold ${review.is_approved ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                    {review.is_approved ? 'Visible' : 'Pending'}
                                </span>
                            </div>

                            <div className="bg-slate-50 rounded-xl p-4 flex-grow mb-4 relative">
                                <span className="absolute top-2 left-2 text-slate-200">
                                    <svg className="w-8 h-8 opacity-50" fill="currentColor" viewBox="0 0 32 32"><path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.896 3.456-8.352 9.12-8.352 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z"></path></svg>
                                </span>
                                <p className="text-slate-700 whitespace-pre-wrap text-sm italic relative z-10 p-2">{review.review_text}</p>
                            </div>

                            <div className="mt-auto border-t border-slate-100 pt-4 flex gap-2">
                                <button
                                    onClick={() => handleToggleApproval(review.id, parseInt(review.is_approved))}
                                    disabled={actionLoading === review.id}
                                    className={`w-full py-2.5 rounded-xl font-bold transition-colors disabled:opacity-50 ${review.is_approved
                                            ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                                            : 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-md shadow-emerald-500/30'
                                        }`}
                                >
                                    {actionLoading === review.id
                                        ? 'Updating...'
                                        : (review.is_approved ? 'Hide Review' : 'Approve & Publish')}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default AdminReviews;
